import { useCallback, useEffect, useState } from "react";
import {
  getLeaderboard,
  getMatchHistory,
  getPlayerProfile,
  type DeviceSession,
  type LeaderboardEntry,
  type MatchHistoryEntry,
  type PlayerProfile,
} from "../game/api";

export interface PlayerHubState {
  status: "idle" | "loading" | "ready" | "error";
  profile: PlayerProfile | null;
  history: MatchHistoryEntry[];
  leaderboard: LeaderboardEntry[];
  error: string | null;
  retry: () => void;
}

export function usePlayerHub(session: DeviceSession | null, active: boolean): PlayerHubState {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<PlayerHubState, "retry">>({
    status: "idle",
    profile: null,
    history: [],
    leaderboard: [],
    error: null,
  });

  useEffect(() => {
    if (!active) return;
    if (!session) {
      setState({ status: "error", profile: null, history: [], leaderboard: [], error: "Online profile service unavailable." });
      return;
    }
    const controller = new AbortController();
    setState((current) => ({ ...current, status: "loading", error: null }));
    void Promise.all([
      getPlayerProfile(session, controller.signal),
      getMatchHistory(session, controller.signal),
      getLeaderboard(session, controller.signal),
    ])
      .then(([profile, history, leaderboard]) => {
        if (controller.signal.aborted) return;
        setState({ status: "ready", profile, history, leaderboard, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState((current) => ({
          ...current,
          status: "error",
          error: error instanceof Error ? error.message : "Player data is unavailable.",
        }));
      });
    return () => controller.abort();
  }, [active, attempt, session]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);
  return { ...state, retry };
}
